import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Clock, Mail, Sparkles, BarChart3 } from 'lucide-react';
import MermaidDiagram from './MermaidDiagram';
import { INNGEST_BRIDGE_CHART, INNGEST_EMAIL_FLOW_CHART } from './inngestGuideCharts';

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{title}</h2>
      <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
        {children}
      </div>
    </section>
  );
}

function JobTable({
  rows,
}: {
  rows: {
    id: string;
    trigger: string;
    schedule: string;
    what: string;
    calls: string;
  }[];
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 not-prose">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-100 dark:bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-500">
            <th className="px-3 py-2">Function</th>
            <th className="px-3 py-2">Trigger</th>
            <th className="px-3 py-2">Schedule / event</th>
            <th className="px-3 py-2">What it does</th>
            <th className="px-3 py-2">Talks to</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {rows.map((row) => (
            <tr key={row.id} className="align-top">
              <td className="px-3 py-2 font-mono text-xs text-violet-700 dark:text-violet-300 whitespace-nowrap">
                {row.id}
              </td>
              <td className="px-3 py-2 whitespace-nowrap">{row.trigger}</td>
              <td className="px-3 py-2 font-mono text-xs">{row.schedule}</td>
              <td className="px-3 py-2">{row.what}</td>
              <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{row.calls}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const toc = [
  { id: 'overview', label: 'Overview' },
  { id: 'bridge', label: 'How Inngest runs here' },
  { id: 'jobs', label: 'All background jobs' },
  { id: 'events', label: 'Events catalog' },
  { id: 'email-flow', label: 'Email-related flow' },
  { id: 'related', label: 'Related guides' },
];

const jobs = [
  {
    id: 'process-scheduled-emails',
    trigger: 'Cron + event',
    schedule: '*/5 * * * * · scheduled-emails/process.requested',
    what: 'Finds pending scheduled_emails with scheduled_at ≤ now (batch default 50, SCHEDULED_EMAIL_SEND_BATCH_SIZE), sends via Postmark. Skips if AI personalization is not ready.',
    calls: 'Postmark, scheduled_emails',
  },
  {
    id: 'send-scheduled-email-now',
    trigger: 'Event only',
    schedule: 'scheduled-emails/send.requested',
    what: 'Sends exactly one row immediately (admin “Send now”). Same send path as the batch worker, with per-row concurrency.',
    calls: 'Postmark, scheduled_emails',
  },
  {
    id: 'personalize-scheduled-emails',
    trigger: 'Cron + event',
    schedule: '*/15 * * * * · scheduled-emails/personalize.requested',
    what: 'Runs AI copy personalization on pending rows in the lead window (default 24h before send). Updates subject/body and personalization JSON status.',
    calls: 'OpenAI, subscribers, scheduled_emails',
  },
  {
    id: 'publish-completed-newsletters',
    trigger: 'Cron + event',
    schedule: '*/5 * * * * · newsletters/publish.requested',
    what: 'Moves newsletters from Ready to Publish → Published when scheduled_for has passed and no pending sends remain.',
    calls: 'newsletters, scheduled_emails',
  },
  {
    id: 'rollup-email-engagement',
    trigger: 'Cron + event',
    schedule: '0 6 * * * · email-engagement/rollup.requested',
    what: 'After the measurement window (default 14 days), aggregates opens/clicks into rollups and deletes raw events for that campaign.',
    calls: 'email_engagement_events, email_engagement_rollups',
  },
  {
    id: 'welcome-email',
    trigger: 'Event only',
    schedule: 'subscriber/created',
    what: 'Enqueues a “Welcome email” scheduled_emails row for a new subscriber (idempotent per subscriber). Actual send is handled by the send workers.',
    calls: 'scheduled_emails, subscribers',
  },
  {
    id: 'auto-materialize-scheduled-newsletters',
    trigger: 'Cron + event',
    schedule: '*/30 * * * * · newsletters/auto-materialize.requested',
    what: 'Safety net: materializes Scheduled newsletters with no scheduled_emails rows within AUTO_MATERIALIZE_LEAD_HOURS (default 24h) of send time.',
    calls: 'materialize-newsletter edge fn, newsletters',
  },
];

const events = [
  {
    name: 'subscriber/created',
    firedBy: 'New subscriber recorded (admin, ingest, subscribe flow, etc.)',
    handledBy: 'welcome-email',
  },
  {
    name: 'scheduled-emails/process.requested',
    firedBy: 'Manual / ops (optional drain)',
    handledBy: 'process-scheduled-emails',
  },
  {
    name: 'scheduled-emails/send.requested',
    firedBy: 'request-send-scheduled-email edge fn (Send now)',
    handledBy: 'send-scheduled-email-now',
  },
  {
    name: 'scheduled-emails/personalize.requested',
    firedBy: 'materialize-newsletter after inserts; manual',
    handledBy: 'personalize-scheduled-emails',
  },
  {
    name: 'newsletters/publish.requested',
    firedBy: 'Manual (faster publish than waiting for cron)',
    handledBy: 'publish-completed-newsletters',
  },
  {
    name: 'email-engagement/rollup.requested',
    firedBy: 'Manual rollup',
    handledBy: 'rollup-email-engagement',
  },
];

export default function InngestGuideView() {
  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-violet-50 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-800">
            <Zap className="w-6 h-6 text-violet-600 dark:text-violet-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Inngest background jobs
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Scheduled and event-driven workers that run on Supabase via the Inngest bridge.
            </p>
          </div>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Related:{' '}
          <Link
            to="/guide/email-pipeline"
            className="text-emerald-600 dark:text-emerald-400 underline font-medium"
          >
            Send pipeline
          </Link>
          {' · '}
          <Link
            to="/guide/edge-functions"
            className="text-orange-600 dark:text-orange-400 underline font-medium"
          >
            Edge functions
          </Link>
        </p>
      </div>

      <nav className="mb-8 flex flex-wrap gap-2 text-xs">
        {toc.map((item) => (
          <a
            key={item.id}
            href={`#${item.id}`}
            className="px-2.5 py-1 rounded-full border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-violet-50 dark:hover:bg-violet-900/20 hover:border-violet-300 dark:hover:border-violet-700 transition-colors"
          >
            {item.label}
          </a>
        ))}
      </nav>

      <div className="space-y-10">
        <Section id="overview" title="Overview">
          <p>
            <strong>Inngest</strong> is our job runner for anything that should not block the admin
            UI or an HTTP request: sending email batches, AI personalization, publishing
            newsletters, and engagement rollups. All workers are registered in one Supabase edge
            function named <code className="text-xs font-mono">inngest</code>.
          </p>
          <p>
            Think of it as: <strong>edge functions handle requests</strong>;{' '}
            <strong>Inngest handles time and retries</strong>.
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-violet-500 shrink-0" />
              Crons run on Inngest’s schedule and call back into your project.
            </li>
            <li className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-emerald-500 shrink-0" />
              Most jobs read/write <code className="text-xs font-mono">scheduled_emails</code> and
              Postmark.
            </li>
            <li className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500 shrink-0" />
              Personalization runs on its own cadence before the send cron.
            </li>
            <li className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-sky-500 shrink-0" />
              Engagement rollup is separate from sending — see the{' '}
              <Link to="/guide/email-tracking" className="underline text-sky-600 dark:text-sky-400">
                tracking guide
              </Link>
              .
            </li>
          </ul>
        </Section>

        <Section id="bridge" title="How Inngest runs here">
          <MermaidDiagram
            chart={INNGEST_BRIDGE_CHART}
            caption="Events are sent with inngest.send from edge code; Inngest Cloud invokes the serve() handler."
          />
          <p>
            <strong>Deploy URL</strong> (register in Inngest dashboard):{' '}
            <code className="text-xs font-mono break-all">
              https://&lt;project-ref&gt;.supabase.co/functions/v1/inngest
            </code>
          </p>
          <p>
            <strong>Secrets</strong> (Supabase edge secrets):{' '}
            <code className="text-xs font-mono">INNGEST_EVENT_KEY</code> (send events),{' '}
            <code className="text-xs font-mono">INNGEST_SIGNING_KEY</code> (verify Inngest → you).
            Deploy with <code className="text-xs font-mono">verify_jwt = false</code> — Inngest does
            not send a Supabase user JWT.
          </p>
          <p>
            Each function uses <strong>concurrency limit 1</strong> where batch safety matters, so
            only one run of that job executes at a time (cron and manual event share the same cap).
          </p>
        </Section>

        <Section id="jobs" title="All background jobs">
          <JobTable rows={jobs} />
        </Section>

        <Section id="events" title="Events catalog">
          <p>
            Event names are typed in{' '}
            <code className="text-xs font-mono">inngest/_shared/client.ts</code>. Only these names
            should be used with <code className="text-xs font-mono">inngest.send</code>.
          </p>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 not-prose">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2">Event</th>
                  <th className="px-3 py-2">Usually fired by</th>
                  <th className="px-3 py-2">Handled by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {events.map((e) => (
                  <tr key={e.name} className="align-top">
                    <td className="px-3 py-2 font-mono text-xs text-violet-700 dark:text-violet-300">
                      {e.name}
                    </td>
                    <td className="px-3 py-2">{e.firedBy}</td>
                    <td className="px-3 py-2 font-mono text-xs">{e.handledBy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section id="email-flow" title="Email-related flow">
          <MermaidDiagram
            chart={INNGEST_EMAIL_FLOW_CHART}
            caption="Solid arrows = typical path. Engagement rollup runs after sends, on a daily schedule."
          />
          <p>
            For step-by-step send states and Postmark details, use the{' '}
            <Link
              to="/guide/email-pipeline"
              className="text-emerald-600 dark:text-emerald-400 underline font-medium"
            >
              send pipeline guide
            </Link>
            .
          </p>
        </Section>

        <Section id="related" title="Related guides">
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <Link to="/guide/email-pipeline" className="underline text-emerald-600 dark:text-emerald-400">
                Email send pipeline
              </Link>{' '}
              — materialize, statuses, send cron
            </li>
            <li>
              <Link to="/guide/email-tracking" className="underline text-sky-600 dark:text-sky-400">
                Open/click tracking
              </Link>{' '}
              — webhooks and rollups (rollup job is listed above)
            </li>
            <li>
              <Link to="/guide/edge-functions" className="underline text-orange-600 dark:text-orange-400">
                Edge functions
              </Link>{' '}
              — HTTP entry points that enqueue Inngest events
            </li>
          </ul>
        </Section>
      </div>
    </div>
  );
}
